import { useAppSelector } from "@app/hooks";
import SkeletonLoader from "@components/SkeletonLoader";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldSelect } from "@emerald-react/select";
import useSurveyState from "@hooks/useSurveyState";
import ShareModal from "@survey-share/ShareModal";
import { setShareModalOpen, updateCurrentSurvey } from "@survey/surveySlice";
import classNames from "classnames";
import { BaseSyntheticEvent, useEffect, useState } from "react";
import { useParams } from "react-router";
import BuilderAccordionSection from "./BuilderAccordionSection";

const BuilderSidebar = () => {
  // Use the reusable survey state hook
  const { survey, dispatch, updateSurvey } = useSurveyState();
  const { surveyId } = useParams();
  const isLoading = useAppSelector((state) => state.survey.isLoading);
  const isShareModalOpen = useAppSelector(
    (state) => state.survey.isShareModalOpen,
  );
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  // Check if survey is loading or survey ID doesn't match (page transition)
  const isSurveyReady = !isLoading && survey && survey.id === surveyId;

  // Monitor for EmeraldSelect and EmeraldSearch dropdown opening/closing
  useEffect(() => {
    const checkDropdown = () => {
      const selectDropdown = document.querySelector(".emerald-select-dropdown");
      const searchDropdown = document.querySelector(
        ".emerald-search__dropdown-wrapper",
      );
      setIsSelectOpen(!!(selectDropdown || searchDropdown));
    };

    const observer = new MutationObserver(checkDropdown);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handlePdfOrientationChange = (e: BaseSyntheticEvent) => {
    dispatch(updateCurrentSurvey({ pdfPaperSize: e.target.innerText }));
    updateSurvey({
      id: survey?.id,
      pdfPaperSize: e.target.innerText,
    });
  };

  const handleShareModalToggle = (isOpen: boolean) => {
    dispatch(setShareModalOpen(isOpen));
  };

  // Close share modal when exiting Builder page
  useEffect(() => {
    return () => {
      dispatch(setShareModalOpen(false));
    };
  }, [dispatch]);

  return (
    <div
      className={classNames(
        "builder-sidebar bg-white border-r sticky border-gray-300 text-data",
        { "overflow-y-auto": isShareModalOpen },
      )}
    >
      {/* ShareModal - always rendered, hidden with CSS when closed */}
      <div className={classNames({ hidden: !isShareModalOpen })}>
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => handleShareModalToggle(false)}
        />
      </div>

      {/* Sidebar content - always rendered, hidden with CSS when Share is open */}
      <div className={classNames({ hidden: isShareModalOpen })}>
        <div
          className={classNames("builder-sections", {
            "overflow-y-hidden!": isSelectOpen,
          })}
        >
          <div className="px-4 mt-5" id="pdf-orientation-select">
            {!isSurveyReady ? (
              <SkeletonLoader className="h-10" />
            ) : (
              <EmeraldSelect
                name="pdfPaperSize"
                label="PDF Orientation"
                value={survey?.pdfPaperSize || "Portrait"}
                options={[
                  { value: "Portrait", label: "Portrait" },
                  { value: "Landscape", label: "Landscape" },
                ]}
                onChange={handlePdfOrientationChange}
                size="small"
              />
            )}
          </div>
          <div className="flex px-4 justify-between text-xs font-normal pl-10">
            <h5>SECTION</h5>
            <h5>OPTIONS</h5>
          </div>
          {!isSurveyReady ? (
            <div className="emerald-accordion-group mb-4 px-4">
              <SkeletonLoader
                className="h-12"
                wrapperClass="gap-2"
                count={5}
              />
            </div>
          ) : (
            <BuilderAccordionSection />
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 shadow-elevation-12 bg-white">
          {!isSurveyReady ? (
            <SkeletonLoader className="h-9" borderRadius="rounded-sm" />
          ) : (
            <EmeraldButton
              label="Share"
              className="w-full"
              onClick={() => handleShareModalToggle(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BuilderSidebar;
